import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  ChevronLeft, Plus, Volume2, VolumeX, RotateCcw,
  Trophy, Crown, Loader2, Home, Target
} from 'lucide-react';
import { useCheckersStore } from './checkersStore';
import { useAuth } from '@/contexts/AuthContext';

// Checkers piece component
const CheckersPiece = ({ piece, isSelected }) => {
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      className={`
        w-[80%] h-[80%] rounded-full relative
        ${piece.color === 'red' 
          ? 'bg-gradient-to-br from-red-500 to-red-700 shadow-lg shadow-red-500/30' 
          : 'bg-gradient-to-br from-gray-800 to-gray-950 shadow-lg shadow-black/50'
        }
        ${isSelected ? 'ring-4 ring-yellow-400 ring-offset-2 ring-offset-transparent' : ''}
        transition-all duration-150
      `}
    >
      {/* Inner circle for depth */}
      <div className={`
        absolute inset-[15%] rounded-full
        ${piece.color === 'red' 
          ? 'bg-gradient-to-br from-red-400 to-red-600' 
          : 'bg-gradient-to-br from-gray-700 to-gray-900'
        }
      `} />
      
      {/* King crown */}
      {piece.isKing && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Crown className={`w-6 h-6 ${piece.color === 'red' ? 'text-yellow-300' : 'text-yellow-400'}`} />
        </div>
      )}
    </motion.div>
  );
};

// Board square component
const Square = ({ row, col, piece, isSelected, isValidMove, isCapture, onClick }) => {
  const isDark = (row + col) % 2 === 1;
  
  return (
    <motion.div
      className={`
        aspect-square flex items-center justify-center relative cursor-pointer
        ${isDark 
          ? 'bg-gradient-to-br from-emerald-800 to-emerald-900' 
          : 'bg-gradient-to-br from-amber-100 to-amber-200'
        }
        ${isSelected ? 'ring-4 ring-inset ring-yellow-400' : ''}
        transition-all duration-150
      `}
      onClick={onClick}
      whileHover={{ brightness: 1.1 }}
    >
      {/* Valid move indicator */}
      {isValidMove && !piece && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className={`
            w-[30%] h-[30%] rounded-full
            ${isCapture 
              ? 'bg-red-500/60 ring-2 ring-red-400' 
              : 'bg-yellow-400/50'
            }
          `}
        />
      )}
      
      {/* Capture indicator on piece */}
      {isValidMove && piece && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute inset-1 rounded-full ring-4 ring-red-500/60"
        />
      )}
      
      {/* Piece */}
      {piece && (
        <CheckersPiece piece={piece} isSelected={isSelected} />
      )}
    </motion.div>
  );
};

// New Game Dialog
const NewGameDialog = ({ isOpen, onClose, onStart, onTriangle }) => {
  const [playerColor, setPlayerColor] = useState('red');
  const [difficulty, setDifficulty] = useState('medium');
  const [variant, setVariant] = useState('AMERICAN');
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-emerald-500/30 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-emerald-400 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-red-500" />
            New Checkers Game
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Variant Selection */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Game Variant</label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Button
                variant={variant === 'AMERICAN' ? 'default' : 'outline'}
                onClick={() => setVariant('AMERICAN')}
                className={variant === 'AMERICAN' ? 'bg-emerald-600' : 'border-slate-600'}
              >
                American (8x8)
              </Button>
              <Button
                variant={variant === 'INTERNATIONAL' ? 'default' : 'outline'}
                onClick={() => setVariant('INTERNATIONAL')}
                className={variant === 'INTERNATIONAL' ? 'bg-emerald-600' : 'border-slate-600'}
              >
                International (10x10)
              </Button>
            </div>
            <Button
              variant="outline"
              onClick={onTriangle}
              className="w-full border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 flex items-center justify-center gap-2"
            >
              <Target className="w-4 h-4" />
              Triangle Checkers (Hex Board)
            </Button>
          </div>
          
          {/* Color Selection */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Play as</label>
            <div className="flex gap-3">
              <button
                onClick={() => setPlayerColor('red')}
                className={`
                  flex-1 p-4 rounded-xl border-2 transition-all
                  ${playerColor === 'red' 
                    ? 'border-red-500 bg-red-500/20' 
                    : 'border-slate-600 hover:border-slate-500'
                  }
                `}
              >
                <div className="w-10 h-10 mx-auto rounded-full bg-gradient-to-br from-red-500 to-red-700 mb-2" />
                <div className="text-sm">Red (First)</div>
              </button>
              <button
                onClick={() => setPlayerColor('black')}
                className={`
                  flex-1 p-4 rounded-xl border-2 transition-all
                  ${playerColor === 'black' 
                    ? 'border-gray-400 bg-gray-500/20' 
                    : 'border-slate-600 hover:border-slate-500'
                  }
                `}
              >
                <div className="w-10 h-10 mx-auto rounded-full bg-gradient-to-br from-gray-800 to-gray-950 mb-2" />
                <div className="text-sm">Black</div>
              </button>
            </div>
          </div>
          
          {/* Difficulty */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">AI Difficulty</label>
            <div className="grid grid-cols-4 gap-2">
              {['easy', 'medium', 'hard', 'expert'].map((d) => (
                <Button
                  key={d}
                  variant={difficulty === d ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDifficulty(d)}
                  className={difficulty === d ? 'bg-emerald-600' : 'border-slate-600'}
                >
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-slate-600">
            Cancel
          </Button>
          <Button 
            onClick={() => onStart({ playerColor, aiDifficulty: difficulty, variant })}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          >
            Start Game
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Game Over Dialog
const GameOverDialog = ({ isOpen, winner, playerColor, onNewGame, onClose }) => {
  const playerWon = winner === playerColor;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-emerald-500/30 text-white max-w-sm text-center">
        <div className="py-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className={`
              w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4
              ${playerWon 
                ? 'bg-gradient-to-br from-yellow-400 to-amber-500' 
                : 'bg-gradient-to-br from-gray-600 to-gray-800'
              }
            `}
          >
            <Trophy className={`w-10 h-10 ${playerWon ? 'text-white' : 'text-gray-400'}`} />
          </motion.div>
          
          <h2 className={`text-2xl font-bold mb-2 ${playerWon ? 'text-yellow-400' : 'text-gray-400'}`}>
            {playerWon ? 'Victory!' : 'Defeat'}
          </h2>
          <p className="text-gray-400">
            {playerWon ? 'Congratulations! You won the game!' : 'Better luck next time!'}
          </p>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-slate-600">
            Review
          </Button>
          <Button onClick={onNewGame} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
            New Game
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Checkers Game Component
const CheckersGame = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const {
    game,
    board,
    currentPlayer,
    gameStatus,
    winner,
    moveHistory,
    selectedPiece,
    validMoves,
    playerColor,
    aiDifficulty,
    soundEnabled,
    isAIThinking,
    newGame,
    selectPiece,
    makeMove,
    setSoundEnabled,
    resetGame
  } = useCheckersStore();
  
  const [showNewGame, setShowNewGame] = useState(gameStatus === 'idle');
  const [showGameOver, setShowGameOver] = useState(false);
  const [dialogDismissed, setDialogDismissed] = useState(false);
  
  useEffect(() => {
    if (gameStatus === 'idle' && !dialogDismissed) {
      setShowNewGame(true);
    }
  }, [gameStatus, dialogDismissed]);
  
  useEffect(() => {
    if (gameStatus === 'gameOver') {
      setShowGameOver(true);
    }
  }, [gameStatus]);
  
  const handleSquareClick = (row, col) => {
    if (isAIThinking || gameStatus !== 'playing') return;
    
    const piece = game?.getPiece(row, col);
    
    if (selectedPiece) {
      // Try to move to this square
      const isValidTarget = validMoves.some(m => m.row === row && m.col === col);
      if (isValidTarget) {
        makeMove(row, col);
      } else if (piece && piece.color === playerColor) {
        // Select different piece
        selectPiece(row, col);
      } else {
        // Deselect
        selectPiece(-1, -1);
      }
    } else if (piece && piece.color === playerColor) {
      selectPiece(row, col);
    }
  };
  
  const handleNewGame = (options) => {
    setShowNewGame(false);
    setDialogDismissed(false);
    newGame(options);
  };
  
  const handleCloseNewGame = () => {
    setShowNewGame(false);
    setDialogDismissed(true);
  };
  
  const boardSize = game?.boardSize || 8;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950/20 to-slate-950">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-emerald-500/20 py-3 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/')}
              className="text-gray-400 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">{isAuthenticated ? 'Dashboard' : 'Home'}</span>
            </Button>
            
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-700" />
              <h1 className="text-xl font-bold text-emerald-400">Checkers</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-gray-400 hover:text-white"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
            
            <Button
              size="sm"
              onClick={() => {
                setDialogDismissed(false);
                setShowNewGame(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">New Game</span>
            </Button>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6 items-start justify-center">
          {/* Board */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-[500px]"
          >
            {/* Turn indicator */}
            <div className="flex items-center justify-between mb-4 px-2">
              <div className={`
                flex items-center gap-2 px-3 py-2 rounded-lg
                ${currentPlayer === 'red' ? 'bg-red-500/20 border border-red-500/30' : 'bg-gray-500/20 border border-gray-500/30'}
              `}>
                <div className={`w-4 h-4 rounded-full ${currentPlayer === 'red' ? 'bg-red-500' : 'bg-gray-800'}`} />
                <span className="text-white text-sm font-medium">
                  {currentPlayer === playerColor ? 'Your Turn' : 'AI Thinking...'}
                </span>
                {isAIThinking && <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />}
              </div>
              
              <div className="text-gray-400 text-sm">
                Move {Math.floor(moveHistory.length / 2) + 1}
              </div>
            </div>
            
            {/* Board grid */}
            <div 
              className="grid gap-0 rounded-lg overflow-hidden shadow-2xl border-4 border-amber-900"
              style={{ gridTemplateColumns: `repeat(${boardSize}, 1fr)` }}
            >
              {Array.from({ length: boardSize }).map((_, row) =>
                Array.from({ length: boardSize }).map((_, col) => {
                  // Flip board if playing as black
                  const displayRow = playerColor === 'black' ? boardSize - 1 - row : row;
                  const displayCol = playerColor === 'black' ? boardSize - 1 - col : col;
                  
                  const piece = board[displayRow]?.[displayCol];
                  const isSelected = selectedPiece?.row === displayRow && selectedPiece?.col === displayCol;
                  const validMove = validMoves.find(m => m.row === displayRow && m.col === displayCol);
                  
                  return (
                    <Square
                      key={`${row}-${col}`}
                      row={displayRow}
                      col={displayCol}
                      piece={piece}
                      isSelected={isSelected}
                      isValidMove={!!validMove}
                      isCapture={validMove?.isCapture}
                      onClick={() => handleSquareClick(displayRow, displayCol)}
                    />
                  );
                })
              )}
            </div>
            
            {/* Game info */}
            <div className="mt-4 flex justify-between items-center px-2 text-sm text-gray-400">
              <div>Playing as: <span className={playerColor === 'red' ? 'text-red-400' : 'text-gray-300'}>{playerColor}</span></div>
              <div>Difficulty: <span className="text-emerald-400 capitalize">{aiDifficulty}</span></div>
            </div>
          </motion.div>
          
          {/* Side panel - Move history */}
          <div className="w-full lg:w-64 bg-slate-900/80 rounded-xl border border-emerald-500/30 p-4">
            <h3 className="text-emerald-400 font-semibold mb-3">Move History</h3>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {moveHistory.length === 0 ? (
                <p className="text-gray-500 text-sm">No moves yet</p>
              ) : (
                moveHistory.map((move, idx) => (
                  <div 
                    key={idx}
                    className={`
                      text-sm px-2 py-1 rounded
                      ${move.player === 'red' ? 'text-red-400' : 'text-gray-300'}
                      ${idx === moveHistory.length - 1 ? 'bg-emerald-500/20' : ''}
                    `}
                  >
                    {Math.floor(idx / 2) + 1}. {move.player}: 
                    ({move.from.row},{move.from.col}) → ({move.to.row},{move.to.col})
                    {move.captured && ' ✕'}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
      
      {/* Dialogs */}
      <NewGameDialog
        isOpen={showNewGame}
        onClose={handleCloseNewGame}
        onStart={handleNewGame}
        onTriangle={() => navigate('/games/triangle-checkers')}
      />
      
      <GameOverDialog
        isOpen={showGameOver}
        winner={winner}
        playerColor={playerColor}
        onNewGame={() => {
          setShowGameOver(false);
          setShowNewGame(true);
        }}
        onClose={() => setShowGameOver(false)}
      />
    </div>
  );
};

export default CheckersGame;
